import { Text, Spacer } from "@hackthenorth/north";
import { Button, Spinner, TextInput } from "north.js";
import React, { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import styled from "styled-components";

import { PageWrapper } from "src/shared/components";
import { errorToast } from "src/shared/components/Toast";
import { Route } from "src/shared/constants/route";
import { useHackerContext } from "src/shared/contexts";
import { HackerStage } from "src/shared/contexts/HackerContext/types";
import { ArrowLeft, WaiverDownload } from "src/static/icons";

import {
  BUILDING_TEXT_MAX_LENGTH,
  HEADER_TEXT_COPY,
  OPTION_SECTIONS,
  SIGN_OPTIONS,
  STAMP_NAME_MAX_LENGTH,
} from "./constants";
import { downloadGraphic } from "./downloadGraphic";
import OptionGroup from "./OptionGroup";
import StampPreview from "./StampPreview";
import { GraphicCustomization, HeaderTextOption, SignOption } from "./types";
import { useFirstName } from "./useFirstName";
import { useGraphicCustomization } from "./useGraphicCustomization";

const HackerRsvpGraphic: React.FC = () => {
  const navigate = useNavigate();
  const firstName = useFirstName();
  const { stage, isFetchingData } = useHackerContext();
  const { customization, setOption } = useGraphicCustomization();
  const [downloading, setDownloading] = useState(false);

  const isRsvpComplete =
    stage === HackerStage.CONFIRMED || stage === HackerStage.CHECKED_IN;

  const onDownload = async () => {
    setDownloading(true);
    try {
      await downloadGraphic(customization, firstName);
    } catch {
      errorToast("Something went wrong generating your graphic — try again!");
    } finally {
      setDownloading(false);
    }
  };

  if (isFetchingData) {
    return (
      <LoadingWrapper>
        <Spinner />
      </LoadingWrapper>
    );
  }

  if (!isRsvpComplete) {
    return <Navigate to={Route.HACKER_RSVP} replace />;
  }

  return (
    <StyledPageWrapper>
      <Text mods="h1 heading">You&apos;re going to Hack the North!</Text>
      <Spacer height={8} />
      <Text mods="grey">
        Personalize your graphic and let everyone know you&apos;re going.
      </Text>
      <Spacer height={32} />
      <Columns>
        <LeftCol>
          <BackLink type="button" onClick={() => navigate(Route.HACKER_RSVP)}>
            <ArrowLeft aria-hidden />
            Back to my RSVP
          </BackLink>
          <Spacer height={24} />
          <StampPreview customization={customization} firstName={firstName} />
          <Spacer height={24} />
          <DownloadButtonWrapper>
            <Button
              color="secondary"
              size="md"
              onClick={onDownload}
              disabled={downloading}
            >
              <ButtonContent>
                <WaiverDownload aria-hidden />
                {downloading ? "Preparing..." : "Download Image"}
              </ButtonContent>
            </Button>
          </DownloadButtonWrapper>
        </LeftCol>
        <RightCol>
          {OPTION_SECTIONS.map((section) => (
            <Section key={section.title}>
              <Text mods="h3 heading">{section.title}</Text>
              <Spacer height={16} />
              {section.groups.map((group) => (
                <React.Fragment key={group.label}>
                  <OptionGroup
                    label={group.label}
                    options={group.options}
                    selected={customization[group.field]}
                    onSelect={(value) =>
                      setOption(
                        group.field,
                        value as GraphicCustomization[typeof group.field]
                      )
                    }
                  />
                  <Spacer height={20} />
                </React.Fragment>
              ))}
            </Section>
          ))}
          <Section>
            <Text mods="h3 heading">Leave a message</Text>
            <Spacer height={16} />
            <OptionGroup
              label="Choose your headline"
              options={[
                {
                  value: HeaderTextOption.IM_GOING,
                  label: HEADER_TEXT_COPY[HeaderTextOption.IM_GOING],
                },
                {
                  value: HeaderTextOption.SEE_YOU,
                  label: HEADER_TEXT_COPY[HeaderTextOption.SEE_YOU],
                },
              ]}
              selected={customization.headerText}
              onSelect={(value) =>
                setOption("headerText", value as HeaderTextOption)
              }
            />
            <Spacer height={20} />
            <OptionGroup
              label="What's on your sign?"
              options={SIGN_OPTIONS}
              selected={customization.signOption}
              onSelect={(value) => setOption("signOption", value as SignOption)}
            />
            {customization.signOption === SignOption.BUILDING && (
              <>
                <Spacer height={12} />
                <TextInput
                  value={customization.buildingText}
                  maxLength={BUILDING_TEXT_MAX_LENGTH}
                  placeholder="a really cool rocketship"
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setOption(
                      "buildingText",
                      e.target.value.slice(0, BUILDING_TEXT_MAX_LENGTH)
                    )
                  }
                />
                <Spacer height={4} />
                <CharHint>
                  {customization.buildingText.length}/{BUILDING_TEXT_MAX_LENGTH}{" "}
                  chars
                </CharHint>
              </>
            )}
            <Spacer height={24} />
            <Text mods="h3 heading">Make your mark on your stamp</Text>
            <Spacer height={16} />
            <TextInput
              value={customization.stampName}
              maxLength={STAMP_NAME_MAX_LENGTH}
              placeholder={firstName || "Your name"}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                setOption(
                  "stampName",
                  e.target.value.slice(0, STAMP_NAME_MAX_LENGTH)
                )
              }
            />
            <Spacer height={4} />
            <CharHint>
              {customization.stampName.length}/{STAMP_NAME_MAX_LENGTH} chars
            </CharHint>
          </Section>
        </RightCol>
      </Columns>
    </StyledPageWrapper>
  );
};

const StyledPageWrapper = styled(PageWrapper)`
  max-width: 1200px;
`;

const LoadingWrapper = styled(PageWrapper)`
  display: flex;
  justify-content: center;
  padding-top: 80px;
`;

const Columns = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 48px;

  ${({ theme }) => theme.mediaQueries.tablet`
    flex-direction: column;
    align-items: stretch;
    gap: 32px;
  `}
`;

const LeftCol = styled.div`
  position: sticky;
  top: 32px;
  flex: 0 0 44%;
  min-width: 0;

  ${({ theme }) => theme.mediaQueries.tablet`
    position: static;
    flex: 1 1 auto;
    width: 100%;
    max-width: 500px;
    margin: 0 auto;
  `}
`;

const RightCol = styled.div`
  flex: 1;
  min-width: 0;
`;

const Section = styled.section`
  &:not(:last-child) {
    margin-bottom: 12px;
  }
`;

const CharHint = styled.div`
  text-align: right;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: ${({ theme }) => theme.globalConstants.fontSize.caption}px;
  color: ${({ theme }) => theme.globalConstants.color.textTertiary};
`;

const BackLink = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  background: none;
  border: none;
  padding: 0;
  cursor: pointer;
  font-family: ${({ theme }) => theme.globalConstants.fontFamily.body};
  font-size: ${({ theme }) => theme.globalConstants.fontSize.body}px;
  font-weight: 500;
  color: ${({ theme }) => theme.globalConstants.color.textDark};

  &:hover {
    text-decoration: underline;
  }
`;

const DownloadButtonWrapper = styled.div`
  display: flex;
  justify-content: center;

  > button {
    padding-top: 10px !important;
    padding-bottom: 10px !important;
  }
`;

const ButtonContent = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 8px;
`;

export default HackerRsvpGraphic;
